// src/components/Services/ServiceSidebarLayout.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';      // ◀️ bullet-icoon
import './ServiceSidebarLayout.css';

export default function ServiceSidebarLayout({ services, pricePrefix = 'Vanaf' }) {
  /* -------- Mobile breakpoint -------- */
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = () => window.matchMedia('(max-width: 991.98px)').matches;
    const onResize = () => setIsMobile(mq());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fmt = (p, f) => `€${Number(p).toFixed(0)} ${f}`;

  /* ================= MOBILE (accordion) ============== */
  if (isMobile) {
    return (
      <section
        className="service-sidebar-section container py-5"
        style={{ position: 'relative', zIndex: 3 }}
      >
        <h2 className="section-heading mb-4 text-center">Lease pakketten</h2>

        <div className="accordion shadow-sm" id="servicesAccordion">
          {services.map((svc, i) => (
            <div className="accordion-item" key={svc.id}>
              <h3 className="accordion-header" id={`h-${svc.slug}`}>
                <button
                  className={`accordion-button fw-semibold${i ? ' collapsed' : ''}`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#c-${svc.slug}`}
                  aria-expanded={!i}
                  aria-controls={`c-${svc.slug}`}
                >
                  {svc.title}
                  <span className="badge price-badge ms-auto">
                    {fmt(svc.price, svc.frequency)}
                  </span>
                </button>
              </h3>
              <div
                id={`c-${svc.slug}`}
                className={`accordion-collapse collapse${!i ? ' show' : ''}`}
                aria-labelledby={`h-${svc.slug}`}
                data-bs-parent="#servicesAccordion"
              >
                <div className="accordion-body">
                  {svc.subtitle && <p className="svc-subtitle">{svc.subtitle}</p>}

                  <ul className="svc-features">
                    {svc.features.map((f) => (
                      <li key={f}>
                        <FontAwesomeIcon icon={faCircle} className="bullet-icon me-2" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {svc.examples?.length > 0 && (
                    <>
                      <p className="mb-1 fw-semibold">Praktijk­voorbeelden</p>
                      <ul className="svc-examples">
                        {svc.examples.map((ex, j) => (
                          <li key={j}>
                            <strong>{ex.client}:</strong> {ex.result}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {svc.ctaLabel && (
                    <a href={svc.ctaHref} className="btn btn-orange w-100 mt-3">
                      {svc.ctaLabel}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  /* ================= DESKTOP (grid) ============== */
  return (
    <section
      className="service-sidebar-section container py-5"
      style={{ position: 'relative', zIndex: 3 }}
    >
      <h2 className="section-heading mb-5 text-center">Lease pakketten</h2>

      <div className="row g-4">
        {services.map((svc) => (
          <div className="col-12 col-md-6 col-lg-4" key={svc.id}>
            <article className="package-card h-100 d-flex flex-column shadow-sm">
              <header>
                <h3 className="svc-title mb-1">{svc.title}</h3>
                {svc.subtitle && <p className="svc-subtitle mb-2">{svc.subtitle}</p>}
                <p className="svc-price h5 mb-3">
                  {pricePrefix} {fmt(svc.price, svc.frequency)}
                </p>
              </header>

              <ul className="svc-features flex-grow-1">
                {svc.features.map((f) => (
                  <li key={f}>
                    <FontAwesomeIcon icon={faCircle} className="bullet-icon me-2" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {svc.examples?.length > 0 && (
                <div className="svc-examples mb-3">
                  <p className="fw-semibold mb-1">Voorbeelden</p>
                  {svc.examples.map((ex, j) => (
                    <p className="example mb-1" key={j}>
                      <strong>{ex.client}:</strong> {ex.result}
                    </p>
                  ))}
                </div>
              )}

              {svc.ctaLabel && (
                <a href={svc.ctaHref} className="btn btn-orange mt-auto align-self-start">
                  {svc.ctaLabel}
                </a>
              )}
            </article>
          </div>
        ))}
      </div>
    </section>
  );
}

ServiceSidebarLayout.propTypes = {
  services: PropTypes.arrayOf(
    PropTypes.shape({
      id:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      slug:      PropTypes.string.isRequired,
      title:     PropTypes.string.isRequired,
      subtitle:  PropTypes.string,
      price:     PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      frequency: PropTypes.string,
      features:  PropTypes.arrayOf(PropTypes.string).isRequired,
      examples:  PropTypes.arrayOf(
        PropTypes.shape({
          client: PropTypes.string,
          result: PropTypes.string,
        }),
      ),
      ctaLabel:  PropTypes.string,
      ctaHref:   PropTypes.string,
    }),
  ).isRequired,
  pricePrefix: PropTypes.string,
};
